import { Col, Pagination } from "react-bootstrap";
import React, { useMemo } from "react";
import { ParamKeyValuePair, useSearchParams } from "react-router-dom";

type Props = {
    count: number;
    next: string | null;
    previous: string | null;
};

const ListPagination = ({ count, next, previous }: Props) => {
    const [searchParams, setSearchParams] = useSearchParams();

    // Memoize entries to avoid recalculating on every render
    const entries = useMemo(() => {
        const entries: ParamKeyValuePair[] = [];
        searchParams.forEach((value, key) => {
            if (key !== 'page') {
                entries.push([key, value]);
            }
        });
        return entries;
    }, [searchParams]);

    // Page handler function
    const handlePageChange = (page: number) => {
        setSearchParams([...entries, ['page', page.toString()]]);
    };

    // Memoize calculations
    const totalPages = useMemo(() => Math.ceil(count / 25), [count]);
    const currentPage = useMemo(() => Math.max(1, parseInt(searchParams.get('page') || '1')), [searchParams]);

    const paginationItems = useMemo(() => {
        const siblingCount = 1;
        const boundaryCount = 1;

        if (totalPages <= 1) {
            return totalPages === 1 ? [1] : [];
        }

        const totalNumbers = siblingCount * 2 + 3 + boundaryCount * 2;
        if (totalPages <= totalNumbers) {
            return Array.from({ length: totalPages }, (_, index) => index + 1);
        }

        const leftSiblingIndex = Math.max(currentPage - siblingCount, boundaryCount + 2);
        const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPages - boundaryCount - 1);
        const showLeftEllipsis = leftSiblingIndex > boundaryCount + 2;
        const showRightEllipsis = rightSiblingIndex < totalPages - boundaryCount - 1;

        const items: Array<number | 'ellipsis'> = [];
        const startPages = Array.from({ length: boundaryCount }, (_, index) => index + 1);
        const endPages = Array.from({ length: boundaryCount }, (_, index) => totalPages - boundaryCount + 1 + index);

        items.push(...startPages);

        if (showLeftEllipsis) {
            items.push('ellipsis');
        } else {
            for (let page = boundaryCount + 1; page < leftSiblingIndex; page += 1) {
                items.push(page);
            }
        }

        for (let page = leftSiblingIndex; page <= rightSiblingIndex; page += 1) {
            items.push(page);
        }

        if (showRightEllipsis) {
            items.push('ellipsis');
        } else {
            for (let page = rightSiblingIndex + 1; page <= totalPages - boundaryCount; page += 1) {
                items.push(page);
            }
        }

        items.push(...endPages);

        return items;
    }, [currentPage, totalPages]);

    // Page boundary checks
    const hasNext = useMemo(() => next !== null, [next]);
    const hasPrevious = useMemo(() => previous !== null, [previous]);

    return (
        <Col className='text-center'>
            <Pagination className='justify-content-center'>
                <Pagination.First
                    onClick={() => handlePageChange(1)}
                    disabled={!hasPrevious}
                />
                <Pagination.Prev
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={!hasPrevious}
                />
                {paginationItems.map((item, index) => {
                    if (item === 'ellipsis') {
                        return <Pagination.Ellipsis key={`ellipsis-${index}`} disabled />;
                    }

                    return (
                        <Pagination.Item
                            key={item}
                            active={item === currentPage}
                            onClick={() => handlePageChange(item)}
                        >
                            {item}
                        </Pagination.Item>
                    );
                })}
                <Pagination.Next
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={!hasNext}
                />
                <Pagination.Last
                    onClick={() => handlePageChange(totalPages)}
                    disabled={!hasNext}
                />
            </Pagination>
        </Col>
    );
};

export default ListPagination;
